import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { deleteMeAvatar, uploadMeAvatar } from '@/api/services/auth.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { useAuthStore } from '@/stores/useAuthStore'
import { meQueryKey } from './useMe'
import type { MeData } from '@/types/api.types'

function updateCachedAvatar(me: MeData | undefined, avatar: string | null): MeData | undefined {
  if (!me) return me
  return {
    ...me,
    avatar: avatar ?? undefined,
  }
}

export function useUploadMeAvatar() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)

  return useMutation({
    mutationFn: (file: File) => uploadMeAvatar(file),
    onSuccess: (data) => {
      queryClient.setQueryData<MeData | undefined>(meQueryKey, (current) =>
        updateCachedAvatar(current, data.avatar)
      )
      setUser((current) =>
        current
          ? {
              ...current,
              avatar: data.avatar ?? undefined,
            }
          : current
      )
      // Avatars also render in admin user lists.
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Profile image updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteMeAvatar() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)

  return useMutation({
    mutationFn: () => deleteMeAvatar(),
    onSuccess: (data) => {
      queryClient.setQueryData<MeData | undefined>(meQueryKey, (current) =>
        updateCachedAvatar(current, data.avatar)
      )
      setUser((current) =>
        current
          ? {
              ...current,
              avatar: undefined,
            }
          : current
      )
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Profile image removed')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
